import { useEffect } from 'react';

/**
 * Class the `html.app-scroll-locked { overflow: hidden }` rule in `@reflex/ui`'s
 * stylesheet keys off. Kept in sync with `ui/src/styles/index.css`.
 */
const SCROLL_LOCK_CLASS = 'app-scroll-locked';

/**
 * Tracks how many mounted surfaces want the lock, so a remount (React strict
 * mode mounts effects twice in development) never drops it while a shell is
 * still on screen.
 */
let lockCount = 0;

/**
 * Locks document scrolling for as long as the calling component is mounted.
 *
 * The app shell is a fixed, full-height (`h-app`) flex layout whose panes scroll
 * internally, so the document itself should never scroll. Letting it scroll is
 * actively harmful on iOS Safari: focusing the chat composer scrolls the whole
 * page up to reveal the input, stranding the conversation above the viewport
 * with the keyboard covering what is left.
 *
 * This is deliberately not a global stylesheet rule. Pages rendered outside the
 * shell — the marketing home page at `/home`, docs, replays — are ordinary
 * document-flow pages taller than the viewport with no inner scroll container,
 * and a global lock leaves them unscrollable below the fold.
 */
export function useDocumentScrollLock(): void {
  useEffect(() => {
    lockCount += 1;
    document.documentElement.classList.add(SCROLL_LOCK_CLASS);
    return () => {
      lockCount -= 1;
      if (lockCount === 0) {
        document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
      }
    };
  }, []);
}
